<template>
  <div class="waiver-signature" :class="`waiver-signature--${size}`">
    <!-- Not participating: no signature is expected -->
    <span v-if="state === 'not-participating'" class="waiver-sig-note text-muted">
      <i class="tim-icons icon-simple-remove mr-1"></i>Not participating
    </span>

    <!-- No signature on file -->
    <span v-else-if="state === 'none'" class="waiver-sig-note text-muted">
      <i class="tim-icons icon-pencil mr-1"></i>No signature on file
    </span>

    <!-- Loading -->
    <div v-else-if="state === 'loading'" class="skel waiver-sig-skel" aria-label="Loading signature"></div>

    <!-- Error -->
    <span v-else-if="state === 'error'" class="waiver-sig-note waiver-sig-error">
      <i class="tim-icons icon-alert-circle-exc mr-1"></i>Signature unavailable
      <a href="#" class="waiver-sig-retry" @click.prevent="load">Retry</a>
    </span>

    <!-- Loaded -->
    <div v-else-if="state === 'loaded'" class="waiver-sig-loaded">
      <div class="waiver-sig-card">
        <el-image
          :src="src"
          :preview-src-list="[src]"
          fit="contain"
          class="waiver-sig-img"
          :alt="altText"
        >
          <div slot="error" class="waiver-sig-imgfail text-muted">
            <i class="tim-icons icon-image-02"></i>
          </div>
        </el-image>
      </div>
      <div v-if="size === 'detail' && (signerName || signedDate || isMinor)" class="waiver-sig-caption">
        <span v-if="isMinor" class="waiver-sig-guardian text-muted">Signed by guardian</span>
        <span v-else-if="signerName" class="text-muted">Signed by {{ signerName }}</span>
        <span v-if="signedDate" class="text-muted">{{ isMinor || signerName ? ' · ' : '' }}{{ signedDate }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { fetchWaiverDetail, renderSignature } from '@/util/waiverSignature'

interface Props {
  waiverId?: number | string | null
  // Pre-decoded data-URL or raw blob; when provided the component renders it
  // directly and skips the network fetch.
  signature?: any
  // Caller's known flag from the list/aggregate payload. When false, the
  // component shows "no signature on file" without fetching.
  hasSignature?: boolean | null
  isMinor?: boolean
  notParticipating?: boolean
  signedDate?: string
  signerName?: string
  size?: 'cell' | 'row' | 'detail'
}

const props = withDefaults(defineProps<Props>(), {
  waiverId: null,
  signature: null,
  hasSignature: null,
  isMinor: false,
  notParticipating: false,
  signedDate: '',
  signerName: '',
  size: 'detail',
})

const state = ref<'idle' | 'loading' | 'loaded' | 'error' | 'none' | 'not-participating'>('idle')
const src = ref('')

const altText = () =>
  props.isMinor
    ? `Guardian signature${props.signerName ? ' for ' + props.signerName : ''}`
    : `Signature${props.signerName ? ' of ' + props.signerName : ''}`

async function load() {
  // No signature expected for non-participants.
  if (props.notParticipating) {
    state.value = 'not-participating'
    return
  }
  // Caller handed us the signature directly — render without a round trip.
  if (props.signature != null) {
    const decoded = renderSignature(props.signature)
    if (decoded) {
      src.value = decoded
      state.value = 'loaded'
    } else {
      state.value = 'none'
    }
    return
  }
  // Caller's flag already tells us there's nothing to fetch.
  if (props.hasSignature === false) {
    state.value = 'none'
    return
  }
  if (props.waiverId == null) {
    state.value = 'none'
    return
  }
  state.value = 'loading'
  try {
    const { waiver } = await fetchWaiverDetail(props.waiverId, { includeSignature: true })
    if (waiver && waiver.signature) {
      src.value = waiver.signature
      state.value = 'loaded'
    } else {
      state.value = 'none'
    }
  } catch (err) {
    // Never let a failed signature fetch take down the surface.
    // eslint-disable-next-line no-console
    console.error('Failed to load waiver signature', err)
    state.value = 'error'
  }
}

onMounted(load)

defineExpose({ load })
</script>

<script lang="ts">
export default { name: 'WaiverSignature' }
</script>

<style scoped lang="scss">
.waiver-signature {
  display: inline-flex;
  flex-direction: column;
}

/* The card is intentionally always white — signatures are black ink and need a
   white field to be visible in either theme. */
.waiver-sig-card {
  background: #ffffff;
  border: 1px solid rgba(125, 125, 125, 0.25);
  border-radius: 0.4285rem;
  padding: 6px 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.waiver-sig-img {
  display: block;
  ::v-deep img {
    display: block;
    max-width: 100%;
    height: auto;
  }
}

.waiver-sig-imgfail {
  font-size: 18px;
  padding: 4px 8px;
}

.waiver-sig-caption {
  margin-top: 6px;
  font-size: 0.75rem;
}

.waiver-sig-note {
  font-size: 0.8rem;
  display: inline-flex;
  align-items: center;
}

.waiver-sig-error {
  color: #ec250d;
}

.waiver-sig-retry {
  margin-left: 8px;
  color: #e14eca;
}

.waiver-sig-skel {
  border-radius: 0.4285rem;
}

/* Size variants */
.waiver-signature--cell {
  .waiver-sig-img ::v-deep img { max-height: 40px; }
  .waiver-sig-skel { width: 96px; height: 40px; }
}
.waiver-signature--row {
  .waiver-sig-img ::v-deep img { max-height: 36px; }
  .waiver-sig-skel { width: 88px; height: 36px; }
}
.waiver-signature--detail {
  .waiver-sig-card { width: 100%; }
  .waiver-sig-img ::v-deep img { max-height: 120px; }
  .waiver-sig-skel { width: 100%; height: 120px; }
}
</style>
